import { useCallback } from 'react';

import { useTelemetry } from '@order/Telemetry';

/**
 * This is the function called by the Stripe SDK to confirm the payment.
 */
export const useStripeConfirmHandler = (params: UseStripeConfirmHandlerParams) => {
  const { isPaymentStatePollingEnabled, changePaymentMethod, createPaymentIntent } = params;

  const { track } = useTelemetry();

  const stripeConfirmHandler = useCallback(
    async (result: PaymentResult, shouldSetupFutureUsage: boolean, callback: Callback) => {
      const { id, paymentMethodType } = result;

      if (!isPaymentStatePollingEnabled) {
        track('checkout.stripe-usage', { event: 'submit_payment_method' });
        callback(SUCCESS_COMPLETE);
        changePaymentMethod(id);
        return;
      }

      const clientSecret = await createPaymentIntent(id, paymentMethodType, shouldSetupFutureUsage);

      if (clientSecret) callback({ clientSecret });
    },
    [changePaymentMethod, createPaymentIntent, isPaymentStatePollingEnabled, track],
  );

  return { stripeConfirmHandler };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type PaymentResult = {
  id: string;
  paymentMethodType: string;
  shouldSetupFutureUsage?: boolean;
};

type Callback = (result: { clientSecret: string }) => void;

type ConfirmPaymentIntent = (
  paymentMethodId: string,
  paymentMethodType: string,
  shouldSetupFutureUsage?: boolean,
) => Promise<string | undefined>;

type UseStripeConfirmHandlerParams = {
  isPaymentStatePollingEnabled: boolean;
  changePaymentMethod: (paymentMethodId: string) => void;
  createPaymentIntent: ConfirmPaymentIntent;
};

// ─── Constants ───────────────────────────────────────────────────────────────

const NO_INTENT_COMPLETE = 'COMPLETE_WITHOUT_CONFIRMING_INTENT';
const SUCCESS_COMPLETE = { clientSecret: NO_INTENT_COMPLETE };
